import { useEffect } from "react";

import { useAtomValue } from "jotai";

import { orderedFindingsAtom } from "../atoms/findingsAtoms";
import { slotAssignmentsSelector } from "../atoms/hangingProtocolAtoms";
import { studyMetadataAtom } from "../atoms/selectionAtoms";
import { loadImagesWithConcurrency } from "../utils/dicomLoader";
import { buildS3ImageIds } from "../utils/dicomMetadataLoader";
import { planFindingSeriesPreload } from "../utils/findingSeriesPreload";
import { orderImageIdsForJump } from "../utils/jumpLoadOrder";
import { logger } from "../utils/logger";

/**
 * Fix C — background-preload finding-series.
 *
 * After the active hanging-protocol panes have had a head start, warm the
 * cornerstone image cache for the series that carry an AI finding, fetching each
 * series' finding slice FIRST (then a small window outward). When the reviewer
 * later clicks that finding, its frames are already decoded, so the jump lands
 * instantly instead of cold-loading the whole series (measured ~9.5s for a
 * ~600-frame CT) — see project_case_viewer_finding_jump_perf memory.
 *
 * Deliberately conservative so it can never degrade the active panes:
 *  - bounded to a few series beyond what's already on screen (planner cap),
 *  - a centered window of frames per series (not the whole series),
 *  - low request concurrency,
 *  - delayed start so the visible panes claim the decode pool first,
 *  - fully abortable on study/layout change + unmount.
 */
const PRELOAD_MAX_SERIES = 3;
const PRELOAD_FRAMES_PER_SERIES = 64;
const PRELOAD_CONCURRENCY = 4;
const PRELOAD_START_DELAY_MS = 3000;

export function useFindingSeriesPreload(studyId: string | undefined): void {
  const studyMetadata = useAtomValue(studyMetadataAtom);
  const findings = useAtomValue(orderedFindingsAtom);
  const slotAssignments = useAtomValue(slotAssignmentsSelector);

  useEffect(() => {
    const cdn = studyMetadata?.canonical_cdn_base_url;
    if (!studyId || !cdn || findings.length === 0) return;
    const series = studyMetadata?.series ?? {};

    const visibleSeriesIds = Object.values(slotAssignments).filter(
      (v): v is string => typeof v === "string" && v.length > 0,
    );
    const plan = planFindingSeriesPreload({
      findings,
      visibleSeriesIds,
      maxSeries: PRELOAD_MAX_SERIES,
    });
    if (plan.length === 0) return;

    const controller = new AbortController();
    const { signal } = controller;
    let cancelled = false;

    const timer = setTimeout(async () => {
      for (const target of plan) {
        if (cancelled || signal.aborted) return;
        const instances = series[target.seriesId];
        if (!instances?.length) continue;
        const orderedIds = orderImageIdsForJump(
          buildS3ImageIds(cdn, instances),
          target.sliceIndex,
        ).slice(0, PRELOAD_FRAMES_PER_SERIES);
        try {
          await loadImagesWithConcurrency(orderedIds, PRELOAD_CONCURRENCY, undefined, signal);
          logger.debug(
            "[useFindingSeriesPreload] warmed seriesId=%s frames=%d aroundSlice=%d",
            target.seriesId,
            orderedIds.length,
            target.sliceIndex,
          );
        } catch (e) {
          if (signal.aborted) return;
          logger.debug(
            "[useFindingSeriesPreload] preload failed seriesId=%s err=%o",
            target.seriesId,
            e,
          );
        }
      }
    }, PRELOAD_START_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
  }, [studyId, studyMetadata, findings, slotAssignments]);
}
